import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Button, ButtonProps } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { RichIcon } from '@ui/components/RichIcon'
import { CheckIcon } from '@ui/icons/Check.icon'
import { ClockIcon } from '@ui/icons/Clock.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import {
  ExpandableRow,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { PropertyDetailResponseType, ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { linkify } from '@utils/linkify'
import { isZero } from '@utils/validations'

import { SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionRequestState } from '@model/SubscriptionRequestState'
import {
  isCancellationRequested,
  isSubscriptionCancel,
  isSubscriptionItemActive,
  isSubscriptionItemCompleted,
  isSubscriptionItemNotActive,
  isSubscriptionItemNotCompleted,
} from '@model/SubscriptionTypes'
import { SubscriptionStatus } from '@model/SubscriptionsDto'
import {
  SubscriptionDetailType,
  SubscriptionRequestBadgeAppearance,
  SubscriptionRequestBadgeState,
  SubscriptionRequestBadgeStatusProps,
  SubscriptionRequestBadgeStatusText,
  SubscriptionRequestButtonAppearance,
  SubscriptionRequestButtonStateKeys,
  SubscriptionRequestButtonStatusKeys,
  TradingPlatformsItemProps,
  TradingPlatformsPackagesProps,
} from '@model/TradingPlatformsDto'
import {
  SubscriptionRequestType,
  SubscriptionTimeInForce,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'
import { ProductServiceModal } from '@shared/modals/ProductService.modal'

import { PendingUnsubscriptionModal } from '../MarketData/modals/PendingUnsubscription.modal'
import { ResubscribeModal } from '../MarketData/modals/Resubscribe.modal'
import {
  hasLevelInCart,
  hasPendingUnsubscriptionRequest,
  hasStartingMessage,
} from '../MarketData/subscriptionUtils'
import { PriceRange } from '../SubscriptionsPriceRange'
import { SubscriptionCQGConfirmationModal } from '../modals/SubscriptionCQGConfirmation.modal'
import {
  SubscriptionTimeInForceModal,
  SubscriptionTimeInForceModalProps,
} from '../modals/SubscriptionTimeInForceType.modal'
import { PLATFORM_TYPE_CQG } from './SubscriptionsTradingPlatforms.page'

const SUBSCRIBE_DEFAULT = 'Subscribe'
const PRIMARY_DEFAULT = 'primary'
const ACTIVE_VIA_PACKAGE_DEFAULT = 'Active Via Bundle'

type SelectedSubscription = TradingPlatformsPackagesProps & {
  selectedSubscriptionDetail?: SubscriptionDetailType
}

interface TimeInForceContext {
  selectedSubscription: SelectedSubscription
}

const convertToDecimal = (value?: number) => (value || 0).toFixed(2)

const getExpandedRowStatus = (item: TradingPlatformsItemProps | TradingPlatformsPackagesProps) => {
  switch (true) {
    case isCancellationRequested(item?.subscriptionRequest):
      return SubscriptionRequestButtonStatusKeys.Resubscribe
    case isSubscriptionCancel(item?.subscriptionRequest):
      return SubscriptionRequestButtonStatusKeys.Cancel
    case isSubscriptionItemNotCompleted(item as TradingPlatformsItemProps) ||
      isSubscriptionItemNotActive(item as TradingPlatformsItemProps):
      return SubscriptionRequestButtonStatusKeys.Subscribe
    case !!item?.activeSubscriptionRequest:
      return SubscriptionRequestButtonStatusKeys.Unsubscribe
    default:
      return null
  }
}

const getNonExpandedRowStatus = (
  item: TradingPlatformsItemProps | TradingPlatformsPackagesProps,
  isOpen?: boolean
) => {
  const statusId = item?.subscriptionRequest?.state?.id
  if (
    isOpen &&
    (statusId === SubscriptionStatus.IN_PROCESS ||
      statusId === SubscriptionStatus.DEDUCTING_FUNDS ||
      statusId === SubscriptionStatus.IN_PROCESS_CANCEL)
  ) {
    return null
  }
  return item?.subscriptionRequest?.state?.name
}

interface StatusButtonProps {
  item: TradingPlatformsItemProps | TradingPlatformsPackagesProps
  isOpen?: boolean
  isNonExpandedRow?: boolean
  setIsSubmitting?: (val: boolean) => void
  handleSubscriptionAction?: (
    item: TradingPlatformsItemProps | TradingPlatformsPackagesProps
  ) => void
  handleDataRefresh?: () => void
  expandedButtonSize?: ButtonProps['size']
  onUnsubscribeConfirmationModal?: (
    item: TradingPlatformsItemProps | TradingPlatformsPackagesProps
  ) => void
  isSubscriptionInCart?: boolean
}

const StatusButton: React.FC<StatusButtonProps> = ({
  item,
  isOpen,
  isNonExpandedRow,
  setIsSubmitting,
  handleSubscriptionAction,
  expandedButtonSize = 'sm',
  handleDataRefresh,
  onUnsubscribeConfirmationModal,
  isSubscriptionInCart,
}) => {
  const { t } = useTranslation()
  const clientApiClient = useApiClient(ClientApiClient)
  const infoModal = useModal(InformationModal, { secondaryLabel: t('Got It') })

  const cancelInfoModal = useModal(InformationModal, {
    title: t('Subscriptions.Cancel Order?'),
    secondaryLabel: t('Subscriptions.Keep Order'),
    primaryLabel: t('Subscriptions.Yes, cancel'),
  })
  const resubscribeModal = useModal(ResubscribeModal)

  const handleOnClickResubscribeConfirm = async () => {
    setIsSubmitting?.(true)
    try {
      await clientApiClient.updateSubscriptionRequest({
        subscriptions: [
          {
            subscriptionDetailId: item?.subscriptionRequest?.subscriptionDetailId,
            subscriptionRequestTypeId: SubscriptionRequestType.Resubscribe,
          },
        ],
        subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
      })
      handleDataRefresh?.()
    } finally {
      setIsSubmitting?.(false)
    }
  }

  const handleCancelAction = async (
    target: TradingPlatformsItemProps | TradingPlatformsPackagesProps
  ) => {
    setIsSubmitting?.(true)
    try {
      const subs =
        'subscriptions' in target
          ? target.subscriptions?.map(({ subscriptionRequest }) => ({
              subscriptionDetailId: subscriptionRequest?.subscriptionDetailId,
              subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
            }))
          : []

      const data: UpdateSubscriptionRequest =
        'subscriptions' in target
          ? {
              subscriptions: subs,
              subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
              overrideOpenPositionsForCancel: true,
            }
          : {
              subscriptions: [
                {
                  subscriptionDetailId: target?.subscriptionRequest?.subscriptionDetailId,
                  subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
                },
              ],
              subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
              overrideOpenPositionsForCancel: true,
            }

      await clientApiClient.updateSubscriptionRequest(data)
      handleDataRefresh?.()
    } finally {
      cancelInfoModal.close()
      setIsSubmitting?.(false)
    }
  }

  const handleStatusClick = async (status?: string | null) => {
    switch (status) {
      case SubscriptionRequestButtonStatusKeys.Cancel:
        cancelInfoModal.open({
          description: () => <p>{t('Subscriptions.Do you really like to cancel the order?')}</p>,
          onPrimaryClick: () => handleCancelAction(item),
          onSecondaryClick: () => cancelInfoModal.close(),
        })
        break
      case SubscriptionRequestButtonStatusKeys.Resubscribe:
        resubscribeModal.open({
          amount: item?.price,
          onConfirm: handleOnClickResubscribeConfirm,
        })
        break
      case SubscriptionRequestButtonStatusKeys.Unsubscribe:
        onUnsubscribeConfirmationModal?.(item)
        break
      default:
        handleSubscriptionAction?.(item)
    }
  }

  const statusName:
    | SubscriptionRequestBadgeState
    | SubscriptionRequestButtonStateKeys
    | undefined
    | null = item?.isActiveViaPackage
    ? ACTIVE_VIA_PACKAGE_DEFAULT
    : getNonExpandedRowStatus(item, isOpen)

  const expandedRowStatus = !isNonExpandedRow ? getExpandedRowStatus(item) : null

  const buttonStatusText = statusName
    ? SubscriptionRequestButtonStatusKeys?.[expandedRowStatus || statusName]
    : SUBSCRIBE_DEFAULT

  const buttonAppearance = statusName
    ? SubscriptionRequestButtonAppearance?.[expandedRowStatus || statusName]
    : PRIMARY_DEFAULT

  const badgeStatusText = statusName && SubscriptionRequestBadgeStatusText?.[statusName]
  const badgeAppearance = statusName && SubscriptionRequestBadgeAppearance?.[statusName]

  if (buttonStatusText) {
    if (isOpen) {
      if (isNonExpandedRow && buttonStatusText !== SubscriptionRequestButtonStatusKeys.Subscribe) {
        return (
          <Button
            variant={buttonAppearance}
            size='sm'
            onClick={() => handleStatusClick(statusName)}
          >
            {t(`Subscriptions.${buttonStatusText}`)}
          </Button>
        )
      }
      return null
    }

    return (
      <Button
        variant={buttonAppearance}
        size={expandedButtonSize}
        onClick={() => handleStatusClick(expandedRowStatus || statusName)}
        disabled={isSubscriptionInCart}
      >
        {t(`Subscriptions.${buttonStatusText}`)}
      </Button>
    )
  }

  return (
    <>
      {badgeStatusText && badgeAppearance && (
        <Badge type={badgeAppearance.type} contrast={badgeAppearance.contrast} size='sm'>
          {t(`Subscriptions.${badgeStatusText}`)}
        </Badge>
      )}
      {item?.isActiveViaPackage && (
        <ActionIcon
          icon={InfoIcon}
          size='x20'
          onClick={() =>
            infoModal.open({
              title: t('Subscriptions.Included in Active Package'),
              description: t('Subscriptions.The product is already included'),
            })
          }
        />
      )}
    </>
  )
}

interface SubscriptionsTradingPlatformsTableProps {
  subscriptions?: TradingPlatformsPackagesProps[]
  productsTradingPlatformsData?: TradingPlatformsItemProps[]
  type: string
  forceRefresh?: () => void
  forceRefreshCartItems?: () => void
  isLoading?: boolean
  onUnsubscribeConfirmationModal: (
    item: TradingPlatformsItemProps | TradingPlatformsPackagesProps
  ) => void
  onSubscriptionInfoModalOpen(): void
  noneActiveSubscriptions: SubscriptionCartItem[]
}

export const SubscriptionsTradingPlatformsTable: React.FC<
  SubscriptionsTradingPlatformsTableProps
> = ({
  subscriptions = [],
  productsTradingPlatformsData = [],
  type,
  isLoading = false,
  forceRefresh,
  forceRefreshCartItems,
  onUnsubscribeConfirmationModal,
  onSubscriptionInfoModalOpen,
  noneActiveSubscriptions,
}) => {
  const { t } = useTranslation()
  const clientApiClient = useApiClient(ClientApiClient)
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  const productServiceModal = useModal(ProductServiceModal)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [subscriptionTimeInForceType, setSubscriptionTimeInForceType] = useState<
    number | undefined
  >()

  const alreadySubscribedInfoModal = useModal(InformationModal, {
    title: t('Subscriptions.Already Subscribed'),
    secondaryLabel: t('Got it!'),
  })
  const mutuallyExclusiveInfoModal = useModal(InformationModal, {
    title: t('Subscriptions.Already Subscribed'),
    secondaryLabel: t('Got it!'),
  })

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const toggleRow = (id: string | number) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))

  const pendingUnsubscriptionModal = useModal(PendingUnsubscriptionModal)

  const getSubscriptionRequestType = (selectedSubscription: SelectedSubscription) =>
    selectedSubscription?.activeSubscriptionRequest ||
    selectedSubscription?.subscriptionRequest?.state.name === SubscriptionRequestState.Pending
      ? SubscriptionRequestType.Update
      : SubscriptionRequestType.Add

  const handleDataRefresh = () => {
    forceRefresh?.()
    forceRefreshCartItems?.()
  }

  const handleSelectSubscriptionTimeInForceType: SubscriptionTimeInForceModalProps['onConfirm'] =
    async (id, context) => {
      const selectedSubscription = (context as TimeInForceContext | undefined)?.selectedSubscription

      if (!selectedSubscription) {
        return
      }

      setSubscriptionTimeInForceType(id)
      try {
        await addSubscriptionItemToCart(id, selectedSubscription)
        await updateSubscriptionRequest(id, selectedSubscription)
      } catch (error: unknown) {
        if (
          error instanceof ResponseError &&
          error.response.response?.data.code === 'subscription_detail_is_excluded'
        ) {
          const props = error.response.response?.data?.properties as
            | Record<string, PropertyDetailResponseType>
            | undefined
          mutuallyExclusiveInfoModal.open({
            description: t('Subscriptions.Bundle is mutually exclusive', {
              parent: props?.ExcludedBySubscriptionDetail?.Name,
              child: props?.SubscriptionDetail?.Name,
            }),
          })
          return
        }
        if (
          error instanceof ResponseError &&
          error.response.response?.data.code === 'subscription_detail_is_excluded_by_bundle'
        ) {
          const props = error.response.response?.data?.properties as
            | Record<string, PropertyDetailResponseType>
            | undefined
          alreadySubscribedInfoModal.open({
            description: t('Subscriptions.Bundle is is already subscribed', {
              parent: props?.BundleParentSubscriptionDetail?.Name,
              child: props?.BundleChildSubscriptionDetail?.Name,
            }),
          })
          return
        }
      } finally {
        setSubscriptionTimeInForceType(undefined)
      }
    }

  const addSubscriptionItemToCart = async (id: number, selected: SelectedSubscription) => {
    if (id === SubscriptionTimeInForce.Immediate && selected?.selectedSubscriptionDetail?.id) {
      await clientApiClient.addSubscriptionItemToCart({
        subscriptionDetailId: selected.selectedSubscriptionDetail.id,
        subscriptionTimeInForceId: id,
        subscriptionRequestTypeId: SubscriptionRequestType.Add,
      })
      handleDataRefresh()
    }
  }

  const updateSubscriptionRequest = async (id: number, selected: SelectedSubscription) => {
    if (
      id === SubscriptionTimeInForce.BeginningOfNextMonth &&
      selected?.selectedSubscriptionDetail?.id
    ) {
      await clientApiClient.updateSubscriptionRequest({
        subscriptions: [
          {
            subscriptionDetailId: selected.selectedSubscriptionDetail.id,
            subscriptionRequestTypeId: getSubscriptionRequestType(selected),
          },
        ],
        subscriptionTimeInForceId: id,
      })
      handleDataRefresh()
    }
  }

  const hasInCart = (pkg: TradingPlatformsPackagesProps | TradingPlatformsItemProps) =>
    hasLevelInCart(pkg, noneActiveSubscriptions)

  const getActiveOrPendingSubscriptionLevel = (pkg: TradingPlatformsPackagesProps) =>
    pkg?.details?.find(
      (detail: SubscriptionDetailType) =>
        detail.activeSubscriptionRequest ||
        detail?.subscriptionRequest?.state.name === SubscriptionRequestState.Pending
    )

  const getSelectedSubscriptionDetail = (
    pkg: TradingPlatformsPackagesProps
  ): SelectedSubscription => {
    const selectedSubscriptionDetail = getActiveOrPendingSubscriptionLevel(pkg) || pkg?.details?.[0]
    return { ...pkg, selectedSubscriptionDetail }
  }

  const timeInForceModal = useModal(SubscriptionTimeInForceModal, {
    subscriptionTimeInForceType,
    onConfirm: handleSelectSubscriptionTimeInForceType,
  })

  const handleSetSelectedSubscription = (pkg: TradingPlatformsPackagesProps | undefined) => {
    if (pkg) {
      const selected = getSelectedSubscriptionDetail(pkg)
      timeInForceModal.open({
        context: { selectedSubscription: selected },
      })
    }
  }

  const cqgConfirmationModal = useModal(SubscriptionCQGConfirmationModal)

  const handleSubscriptionConfirmationAction = (pkg: TradingPlatformsPackagesProps) => {
    if (type === PLATFORM_TYPE_CQG && isZero(pkg?.price)) {
      cqgConfirmationModal.open({
        onConfirm: async () => {
          await addSubscriptionItemToCart(
            SubscriptionTimeInForce.Immediate,
            getSelectedSubscriptionDetail(pkg)
          )
        },
      })
    } else {
      handleSetSelectedSubscription(pkg)
    }
  }

  return (
    <Loading showLoadingIcon isLoading={isLoading || isSubmitting}>
      <Table
        isLoading={isLoading}
        noResults={
          !productsTradingPlatformsData.length && (
            <TableNoResults title={t('Subscriptions.No products list')} />
          )
        }
      >
        <TableHeader>
          <TableRow>
            <TableHead align='left'>{t('Subscriptions.Product')}</TableHead>
            <TableHead align='left'>{t('Subscriptions.Price')}</TableHead>
            <TableHead align='center'>{t('Subscriptions.Status')}</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {productsTradingPlatformsData.map((product) => {
            const productPackages = subscriptions.filter(
              (pkg) => pkg?.productType === product?.type
            )
            const id = product.id.toString()
            const isExpanded = expandedRows[id]

            return (
              <ExpandableRow
                key={id}
                row={{
                  ...product,
                  id,
                  subRows: productPackages.map((pkg) => ({ ...pkg, id: pkg.id.toString() })),
                }}
                isExpanded={isExpanded}
                hasTruncate={false}
                onToggle={() => toggleRow(id)}
                renderParent={(p) => {
                  const inCart = hasInCart(p)
                  return [
                    {
                      value: (
                        <Box flex direction='column' gap='x4'>
                          <Body2 medium>
                            <Link
                              to='#'
                              onClick={(event) => {
                                event.preventDefault()
                                productServiceModal.open({
                                  data: {
                                    name: p?.name,
                                    imageUrl: (p as any)?.imageUrl,
                                    imageUrlMobile: (p as any)?.imageUrlMobile,
                                    description: (p as any)?.description,
                                    url: (p as any)?.url,
                                  },
                                })
                              }}
                            >
                              {p?.name}
                            </Link>
                          </Body2>
                          {p?.subName && (
                            <Body2 wrap='wrap' color='text-med-em'>
                              {p.subName}
                            </Body2>
                          )}
                        </Box>
                      ),
                      align: 'left',
                    },
                    {
                      value: !isExpanded ? (
                        <Body2>
                          <PriceRange minAmount={p?.minAmount} maxAmount={p?.maxAmount} />
                        </Body2>
                      ) : undefined,
                      align: 'left',
                    },
                    {
                      value: (
                        <StatusButton
                          isNonExpandedRow
                          item={p}
                          isOpen={isExpanded}
                          handleSubscriptionAction={() => toggleRow(id)}
                          isSubscriptionInCart={inCart}
                        />
                      ),
                      align: 'center',
                    },
                  ]
                }}
                renderSubRow={(pkg: TradingPlatformsPackagesProps) => {
                  const status = (
                    pkg?.isActiveViaPackage
                      ? ACTIVE_VIA_PACKAGE_DEFAULT
                      : pkg?.subscriptionRequest?.state?.name
                  ) as SubscriptionRequestBadgeStatusProps

                  const isActive =
                    isSubscriptionItemCompleted(pkg as TradingPlatformsItemProps) ||
                    isSubscriptionItemActive(pkg as TradingPlatformsItemProps)

                  const badgeStatusText = status && SubscriptionRequestBadgeStatusText?.[status]
                  const [details] = pkg.details || []
                  const dueDate = pkg?.subscriptionRequest?.dueDate
                  const priceText =
                    isZero(pkg?.price) && type === PLATFORM_TYPE_CQG
                      ? t('Subscriptions.Price on Application')
                      : `$${convertToDecimal(pkg?.price)}/${t('Subscriptions.mth')}`

                  const showPending = hasPendingUnsubscriptionRequest(pkg)
                  const showStarting = !showPending && hasStartingMessage(pkg)

                  const inCart = hasInCart(pkg)

                  return [
                    {
                      value: (
                        <Box flex direction='row' gap='x4'>
                          {isActive && (
                            <Box>
                              <RichIcon icon={CheckIcon} size='x20' />
                            </Box>
                          )}
                          {badgeStatusText === 'Pending' && (
                            <Box>
                              <RichIcon
                                icon={{ icon: ClockIcon, color: 'text-warning-high-em' }}
                                size='x20'
                              />
                            </Box>
                          )}
                          {details?.name === 'CQG INTEGRATED CLIENT' && (
                            <Box>
                              <ActionIcon
                                icon={InfoIcon}
                                size='x20'
                                onClick={() => onSubscriptionInfoModalOpen()}
                              />
                            </Box>
                          )}
                          <Box pl='x16' flex direction='column' gap='x4'>
                            <Body2 medium>{details?.name}</Body2>
                            {details?.description && (
                              <Body2 wrap='wrap' color='text-med-em'>
                                {linkify(details.description)}
                              </Body2>
                            )}
                          </Box>
                        </Box>
                      ),
                      align: 'left',
                    },
                    {
                      value: (
                        <Box flex direction='column'>
                          <Box flex direction='row'>
                            <Box>
                              <Body2>{priceText}</Body2>
                            </Box>
                            <Box mx='x6'>
                              {showPending && (
                                <>
                                  <Body2 color='text-success-base-em'>
                                    {dueDate &&
                                      t('Subscriptions.Active until', {
                                        date: formatDate(dueDate, { formatType: dateFormat }),
                                      })}
                                  </Body2>
                                  <Button.Ghost
                                    size='sm'
                                    onClick={() =>
                                      pendingUnsubscriptionModal.open({
                                        dueDate: pkg?.subscriptionRequest?.dueDate,
                                      })
                                    }
                                  >
                                    {t('Subscriptions.Pending Unsubscription')}
                                  </Button.Ghost>
                                </>
                              )}
                            </Box>
                          </Box>

                          <Box>
                            {showStarting && (
                              <Body2>
                                {dueDate &&
                                  t('Subscriptions.Starting', {
                                    firstDateOfNextMonth: formatDate(dueDate, {
                                      formatType: dateFormat,
                                    }),
                                  })}
                              </Body2>
                            )}
                          </Box>
                        </Box>
                      ),
                      align: 'left',
                    },
                    {
                      value: (
                        <StatusButton
                          item={pkg}
                          handleSubscriptionAction={() => handleSubscriptionConfirmationAction(pkg)}
                          handleDataRefresh={handleDataRefresh}
                          setIsSubmitting={setIsSubmitting}
                          onUnsubscribeConfirmationModal={onUnsubscribeConfirmationModal}
                          isSubscriptionInCart={inCart}
                        />
                      ),
                      align: 'center',
                    },
                  ]
                }}
              />
            )
          })}
        </TableBody>
      </Table>
    </Loading>
  )
}
